import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../lib/api';
import { CheckCircle2, XCircle, Clock, UserCheck, Undo2, AlertTriangle, UserCog } from 'lucide-react';

const STEP_STYLE = {
  PENDING: { icon: Clock, color: 'text-muted-foreground', bg: 'bg-muted' },
  APPROVED: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  REJECTED: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  SENT_BACK: { icon: Undo2, color: 'text-orange-600', bg: 'bg-orange-50' },
  SKIPPED: { icon: Clock, color: 'text-muted-foreground', bg: 'bg-muted/50 opacity-60' },
};

export function ApprovalChain({ request, steps, currentUserId, onApprove, onReject, onSendBack, onReassign }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [users, setUsers] = useState(null);
  const { isAdmin } = useAuth();
  const isMyTurn = request.status === 'SUBMITTED' && request.current_approver_id === currentUserId;

  const act = async (decision) => {
    if ((decision === 'REJECTED' || decision === 'SEND_BACK') && !comment.trim()) return;
    setBusy(true);
    try {
      if (decision === 'APPROVED') await onApprove(comment.trim());
      else if (decision === 'REJECTED') await onReject(comment.trim());
      else await onSendBack(comment.trim());
      setComment('');
    } finally {
      setBusy(false);
    }
  };

  const openReassign = async () => {
    setReassignOpen(true);
    if (!users) {
      try {
        const res = await userAPI.list();
        const chainUserIds = new Set((steps || []).map(s => s.approver_user_id));
        setUsers((res.data.users || res.data || []).filter(u => u.is_active && u.id !== request.created_by && !chainUserIds.has(u.id)));
      } catch { setUsers([]); }
    }
  };

  const submitReassign = async () => {
    if (!reassignTo || !reassignReason.trim()) return;
    setBusy(true);
    try {
      await onReassign(reassignTo, reassignReason.trim());
      setReassignOpen(false); setReassignTo(''); setReassignReason('');
    } finally { setBusy(false); }
  };

  if (!steps || steps.length === 0) return null;

  return (
    <div className="rounded-sm border border-border p-3" data-testid="vp-approval-chain">
      <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Approval Chain</p>

      {request.hierarchy_gap && (
        <div className="flex items-start gap-1.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 mb-2" data-testid="vp-hierarchy-gap-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Hierarchy gap: the CRM reporting chain didn't naturally reach the final approver, so this request was routed directly to them. The reporting hierarchy may need fixing.</span>
        </div>
      )}

      <div className="space-y-1.5">
        {steps.map((s, idx) => {
          const style = STEP_STYLE[s.status] || STEP_STYLE.PENDING;
          const Icon = style.icon;
          const showRoundLabel = (s.round || 1) > 1 && (idx === 0 || steps[idx - 1].round !== s.round);
          return (
            <div key={s.id}>
              {showRoundLabel && (
                <p className="text-[10px] uppercase text-muted-foreground mt-2 mb-1">Round {s.round} (after send-back)</p>
              )}
              <div className={`flex items-center gap-2 rounded-sm px-2 py-1.5 ${style.bg}`} data-testid={`vp-step-${s.round || 1}-${s.level}`}>
                <Icon className={`h-4 w-4 shrink-0 ${style.color}`} />
                <span className="text-sm">Level {s.level} — {s.approver_name}</span>
                {s.reassigned_from_name && (
                  <span className="text-[10px] text-muted-foreground italic">(reassigned from {s.reassigned_from_name})</span>
                )}
                <span className={`text-[10px] uppercase ml-auto ${style.color}`}>{s.status}</span>
                {s.comment && <span className="text-xs text-muted-foreground italic ml-2 max-w-[220px] truncate" title={s.comment}>"{s.comment}"</span>}
              </div>
            </div>
          );
        })}
      </div>

      {isMyTurn && (
        <div className="mt-3 pt-3 border-t border-border" data-testid="vp-my-approval-action">
          <div className="flex items-center gap-1.5 text-sm text-primary font-medium mb-2">
            <UserCheck className="h-4 w-4" />It's your turn to review this request
          </div>
          <Textarea
            value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Optional for approval, required to reject or send back..."
            rows={2} className="text-sm" data-testid="vp-approval-comment-input"
          />
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button size="sm" className="rounded-sm" disabled={busy} onClick={() => act('APPROVED')} data-testid="vp-approve-btn">
              <CheckCircle2 className="h-4 w-4 mr-1.5" />Approve
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm text-orange-600 border-orange-200" disabled={busy || !comment.trim()} onClick={() => act('SEND_BACK')} data-testid="vp-send-back-btn">
              <Undo2 className="h-4 w-4 mr-1.5" />Send Back
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200" disabled={busy || !comment.trim()} onClick={() => act('REJECTED')} data-testid="vp-reject-btn">
              <XCircle className="h-4 w-4 mr-1.5" />Reject
            </Button>
          </div>
        </div>
      )}

      {isAdmin && request.status === 'SUBMITTED' && (
        <div className="mt-3 pt-3 border-t border-border">
          {!reassignOpen ? (
            <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={openReassign} data-testid="vp-open-reassign-btn">
              <UserCog className="h-3.5 w-3.5 mr-1.5" />Admin: reassign this step
            </Button>
          ) : (
            <div className="space-y-2" data-testid="vp-reassign-form">
              <select
                value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                className="w-full h-8 text-sm rounded-sm border border-input bg-background px-2"
                data-testid="vp-reassign-select"
              >
                <option value="">Select new approver…</option>
                {(users || []).map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
              <Textarea
                value={reassignReason} onChange={(e) => setReassignReason(e.target.value)}
                placeholder="Reason for reassigning (required)…" rows={2} className="text-sm"
                data-testid="vp-reassign-reason-input"
              />
              <div className="flex gap-2">
                <Button size="sm" className="rounded-sm" disabled={busy || !reassignTo || !reassignReason.trim()} onClick={submitReassign} data-testid="vp-reassign-confirm-btn">
                  Reassign
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setReassignOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
